import { useController } from 'react-hook-form';
import { FormCheckbox, FormGroup } from 'ts/components/Form';
import { EBlacklistingOption } from 'typedefs/EBlacklistingOption';

/** Values provided by the `FlaggingOptionFilter` component. */
export type FlaggingOptionFilterValues = {
	flaggingOption: EBlacklistingOption;
};

type FlaggingOptionFilterProps = {
	enabled: boolean;
};

/**
 * Provides toggles to select the inclusion and exclusion of tolerated and false-positive findings. The component will
 * only work when wrapped in a FormProvider.
 */
export function FlaggingOptionFilter({ enabled }: FlaggingOptionFilterProps) {
	const controller = useController<FlaggingOptionFilterValues, 'flaggingOption'>({ name: 'flaggingOption' });

	if (!enabled) {
		return null;
	}

	const findFlaggingOption = (showNonFlagged: boolean, showFalsePositives: boolean, showTolerations: boolean) =>
		EBlacklistingOption.values.find(
			option =>
				option.showNonFlagged === showNonFlagged &&
				option.showFalsePositives === showFalsePositives &&
				option.showTolerations === showTolerations
		)!;

	return (
		<FormGroup grouped as="fieldset">
			<legend>Excluded Findings</legend>
			<FormCheckbox
				id="false-positives-toggle"
				checked={!controller.field.value.showFalsePositives}
				onChange={(_, { checked }) =>
					controller.field.onChange(
						findFlaggingOption(
							controller.field.value.showNonFlagged,
							!checked,
							controller.field.value.showTolerations
						)
					)
				}
				label="Hide false positives"
			/>
			<FormCheckbox
				id="tolerated-toggle"
				checked={!controller.field.value.showTolerations}
				onChange={(_, { checked }) =>
					controller.field.onChange(
						findFlaggingOption(
							controller.field.value.showNonFlagged,
							controller.field.value.showFalsePositives,
							!checked
						)
					)
				}
				label="Hide tolerated findings"
			/>
			<FormCheckbox
				id="non-flagged-toggle"
				checked={!controller.field.value.showNonFlagged}
				onChange={(_, { checked }) =>
					controller.field.onChange(
						findFlaggingOption(
							!checked,
							controller.field.value.showFalsePositives,
							controller.field.value.showTolerations
						)
					)
				}
				label="Hide not-excluded findings"
			/>
		</FormGroup>
	);
}
